import { CompactText } from 'ui/CompactText';
import type React from 'react';
import { useCallback, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';

import { getCroppedImageFile } from 'utils/getCroppedImageFile';

import styles from './AvatarCropModal.module.scss';

type AvatarCropModalProps = {
  imageSrc: string;
  fileName?: string;
  aspect?: number;
  onCancel: () => void;
  onComplete: (file: File) => void;
};

export const AvatarCropModal: React.FC<AvatarCropModalProps> = ({
  imageSrc,
  fileName = 'avatar.jpg',
  aspect = 3 / 4,
  onCancel,
  onComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    setError(null);
    try {
      const file = await getCroppedImageFile(imageSrc, croppedAreaPixels, fileName);
      onComplete(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось обрезать фото');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.overlay} role='dialog' aria-modal='true' aria-label='Обрезка фото'>
      <div className={styles.modal}>
        <h3 className={styles.title}>Выберите область фото</h3>
        <CompactText className={styles.hint} tone={'info'}>Пропорции 3:4 - так фото будет выглядеть в анкете.</CompactText>
        <div className={styles.cropArea}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid
          />
        </div>
        <label className={styles.zoomRow}>
          <span>Масштаб</span>
          <input
            type='range'
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            disabled={busy}
          />
        </label>
        {error ? <p className={styles.error}>{error}</p> : null}
        <div className={styles.actions}>
          <button type='button' className={styles.secondary} onClick={onCancel} disabled={busy}>
            Отмена
          </button>
          <button type='button' className={styles.primary} onClick={() => void handleApply()} disabled={busy}>
            {busy ? 'Сохранение...' : 'Применить'}
          </button>
        </div>
      </div>
    </div>
  );
};
